import { IProgramId } from '@ucheba/store/institutions/programs/programId/types'
import { useCallback, useMemo } from 'react'
import { useRouter } from 'next/router'
import { stringifyUrl } from '@ucheba/utils/helpers/router'
import { EProgramTypes } from '@ucheba/utils/types/program'
import { useSelector } from 'react-redux'
import { institutionsProgramIdSelectors } from '@ucheba/store/institutions/programs/programId'

interface IStudyOptions {
  (
    trainingForms: IProgramId['trainingForms'],
    programType?: number
  ): {
    activeIndex: number
    onClick: (_: any, slug: string) => void
    filteredList: {
      id: number
      trainingFormType: {
        name: string
        value: number
      }
      price: number
      hasBudget: boolean
      name: string
      isPricePerYear: boolean
      academicHours: number
      duration: string
      budgetExamPoints: number
      averageBySubjectExamScore: number
      budgetPlacesCount: number
      paidPlacesCount: number
      paidPassingScore: number
      schedule: string | null
      nearestDate: {
        dateFrom: string
        isMonthStartAt: boolean
        dateTo: string | null
      } | null
      startType: {
        name: string
        value: 'date' | 'group_is_filled' | 'any_time'
      }
      baseEducationType: {
        name: string
        value: number
      } | null
      isFree?: boolean | null
    }[]
    title: string
    items: {
      name: string
      slug: string
    }[]
  }
}

export const useStudyOptions: IStudyOptions = (trainingForms, programType) => {
  const { query, push, asPath } = useRouter()
  const program = useSelector(institutionsProgramIdSelectors.entity)

  const educationType = useMemo(() => {
    if (query.educationType) {
      return query.educationType
    }

    const arr = asPath.split('/')

    if (arr[arr.length - 1] !== String(program?.id)) {
      return arr[arr.length - 1]
    }

    return ''
  }, [asPath, program?.id, query.educationType])

  const baseEducationTypes = useMemo(() => {
    return trainingForms.reduce((acc, item) => {
      if (item?.baseEducationType?.value) {
        if (acc[item?.baseEducationType?.value]) {
          return acc
        }

        acc[item?.baseEducationType?.value] = item.baseEducationType
      }

      return acc
    }, {})
  }, [trainingForms])

  const items = useMemo(() => {
    return Object.keys(baseEducationTypes).map((key) => {
      return {
        name: baseEducationTypes[key]?.name?.toLowerCase() || '',
        slug: String(baseEducationTypes[key].value),
      }
    })
  }, [baseEducationTypes, asPath])

  const activeIndex = useMemo(() => {
    if (educationType) {
      return items.findIndex((item) => item.slug === educationType) || 0
    }
    return 0
  }, [educationType, items])

  const onClick = useCallback(
    (_: any, slug: string) => {
      if (educationType === slug) return

      push(stringifyUrl(`/program/${program?.id}/${slug}`, {}), undefined, {
        shallow: true,
        scroll: false,
      })
    },
    [educationType, program?.id, push]
  )

  const title = useMemo(() => {
    if (items && items.length === 1) {
      const item = items[0]

      // костыль чтоб исключить для магистратур и аспирантуры второе высшее
      if (
        programType === EProgramTypes.master ||
        programType === EProgramTypes.aspirant
      ) {
        return 'Варианты обучения'
      }

      if (item.name) {
        return `Варианты обучения ${item.name.toLowerCase()}`
      }
    }
    return 'Варианты обучения'
  }, [items, programType])

  const filteredList = useMemo(() => {
    return trainingForms.filter((trainingForm) => {
      if (trainingForm?.baseEducationType?.value) {
        return String(trainingForm.baseEducationType?.value) === items[activeIndex]?.slug
      }

      return true
    })
  }, [trainingForms, items, activeIndex])

  return {
    items,
    activeIndex,
    onClick,
    title,
    filteredList,
  }
}
